/**
 * Dialog "Cambia data sopralluogo" — wrapper sottile su SiteVisitCalendar.
 *
 * Riservato al PRO (marketplace). Il backend applica il vincolo 12h.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarBlank, CheckCircle, Clock } from "@phosphor-icons/react";
import SiteVisitCalendar, { slotToIso, fmtSlot } from "@/components/SiteVisitCalendar";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function RescheduleVisitDialog({ open, onClose, practice, onDone }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [pickedOperatorId, setPickedOperatorId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("picking");

  useEffect(() => {
    if (!open) return;
    setSelectedSlot(null);
    setPickedOperatorId(null);
    setResult(null);
    setError("");
    setPhase("picking");
  }, [open]);

  const submit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setError("");
    try {
      const body = { datetime: slotToIso(selectedSlot) };
      if (pickedOperatorId) body.operator_id = pickedOperatorId;
      const pid = practice._id || practice.id;
      const r = await api.post(`/practices/${pid}/reschedule-visit`, body);
      setResult(r.data);
      setPhase("success");
      toast.success("Data del sopralluogo aggiornata con successo");
      if (onDone) onDone();
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore durante il cambio data";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="reschedule-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarBlank size={18} weight="bold" className="text-blue-600" />
            Cambia data sopralluogo
          </DialogTitle>
          <DialogDescription>
            {phase === "success" ? "Cambio data confermato" : "Seleziona una nuova data e ora"}
          </DialogDescription>
        </DialogHeader>

        {phase === "picking" && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs flex items-center gap-2">
            <Clock size={14} weight="fill" className="text-amber-700" />
            <span className="text-slate-700">
              Data corrente: <b>{fmtDateTime(practice?.scheduled_appointment)}</b>
              {" — "}
              <span className="text-slate-500">(modifica possibile fino a 12h prima)</span>
            </span>
          </div>
        )}

        {phase === "picking" ? (
          <SiteVisitCalendar
            practice={practice}
            testidPrefix="resched"
            hideOperatorName={true}
            askPreviousOperator={false}
            onSlotChange={(slot, opId) => {
              setSelectedSlot(slot);
              setPickedOperatorId(opId);
            }}
          />
        ) : (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <CheckCircle size={32} weight="fill" className="text-emerald-600 shrink-0" />
              <div>
                <div className="font-semibold text-emerald-900" data-testid="resched-success-title">
                  Data cambiata con successo!
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  {result?.operator_changed
                    ? "Il vecchio e il nuovo operatore sono stati notificati via app ed email."
                    : "Il site operator è stato notificato via app ed email."}
                </div>
              </div>
            </div>
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div><b>Nuova data:</b> {fmtDateTime(result?.new_datetime)}</div>
              <div><b>Operatore:</b> {result?.operator_name || "—"}</div>
              {result?.operator_changed && (
                <div className="text-xs text-amber-700 mt-1">
                  ⚠ Cambio operatore: il precedente ha ricevuto notifica di rimozione.
                </div>
              )}
            </div>
          </div>
        )}

        {error && phase !== "success" && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <DialogFooter>
          {phase === "success" ? (
            <Button onClick={onClose} className="ht-gradient border-0" data-testid="resched-success-close">
              Chiudi
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button
                onClick={submit}
                disabled={!selectedSlot || submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="resched-confirm-btn"
              >
                {submitting ? "Invio…" : "Conferma nuova data"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
